<script setup>
import { onMounted, ref, computed } from 'vue';
import { useStore } from 'vuex';
import { formatTimestamp } from '../../helpers';
import Loading from '../miscellaneous/Loading.vue';
import MarketProducts from './MarketProducts.vue';
import MarketSearch from './MarketSearch.vue';
import SpecialReportsTimeline from './SpecialReportsTimeline.vue';
import { useRouter } from 'vue-router';

const store = useStore();
const loading = ref(false);
const searchProductGroupsValue = ref('');
const favoriteShow = ref(false);
const specialNewsShow = ref(false);
const seenContentstreams = ref(false);
const favoriteContentstreams = ref([]);
const specialAnalyses = ref([]);
const specialReportsShow = ref(false);
const marketProductsShow = ref(true);
const specialReports = ref(false);
const specialReportsYears = ref([]);
const router = useRouter();
const region = computed(() => window.meta?.region_configuration?.region || 'de');

const SetAllTapsToFalse = () => {
    marketProductsShow.value = false;
    favoriteShow.value = false;
    specialNewsShow.value = false;
    specialReportsShow.value = false;
};

/**
 * Get the background color of the product group
 * @param {Object} productGroup
 * @param {Boolean} lighter
 * @returns {String}
 */
function getBackgroundColor(productGroup, lighter = false) {
    if (!productGroup || !productGroup.data || productGroup.data.length === 0) {
        return '#FFFFFF';
    }

    return lighter ? lightenColor(productGroup.data[0].background_color, 0.9) : productGroup.data[0].background_color;
}

/**
 * Lighten the color by x%
 * @param {String} hex
 * @param {Float} percent
 * @returns {String}
 */
function lightenColor(hex, percent) {
    // Remove the hash at the start if it's there
    hex = hex.replace(/^#/, '');

    // Parse the r, g, b values from the hex string
    let r = parseInt(hex.substring(0, 2), 16);
    let g = parseInt(hex.substring(2, 4), 16);
    let b = parseInt(hex.substring(4, 6), 16);

    // Calculate the amount to lighten
    r = Math.min(255, Math.round(r + (255 - r) * percent));
    g = Math.min(255, Math.round(g + (255 - g) * percent));
    b = Math.min(255, Math.round(b + (255 - b) * percent));

    // Convert back to hex and return
    return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1).toUpperCase()}`;
}

const getSpecialAnalyses = async () => {
    if (store.getters['settings/isContentLoaded']('specials')) {
        specialAnalyses.value = await store.getters['contentstreams/getByKey']('specials').data;
    } else {
        await store
            .dispatch('contentstreams/specials', {
                params: {
                    'sort[by]': 'published_at',
                    'sort[order]': 'desc',
                    page_size: 80,
                    page: 1,
                    key: 'specials'
                }
            })
            .then((response) => {
                store.commit('settings/contentLoaded', 'specials');
                specialAnalyses.value = response.data;
            });
    }
};

const getSpecialReports = async () => {
    const setSpecialReportsYears = (reports) => {
        specialReportsYears.value = reports.map((report) => {
            return new Date(report.published_at).getFullYear();
        });

        specialReportsYears.value = [...new Set(specialReportsYears.value)];
    };

    if (store.getters['settings/isContentLoaded']('special-reports')) {
        store.getters['contentstreams/getByKey']('special-reports');
        specialReports.value = await store.getters['contentstreams/getByKey']('special_reports').data;

        setSpecialReportsYears(specialReports.value);
    } else {
        await store
            .dispatch('contentstreams/specialReports', {
                params: {
                    'sort[by]': 'published_at',
                    'sort[order]': 'desc',
                    page_size: 100,
                    page: 1,
                    key: 'special_reports'
                }
            })
            .then((response) => {
                store.commit('settings/contentLoaded', 'special-reports');
                specialReports.value = response.data;

                setSpecialReportsYears(response.data);
            });
    }
};

const getFavoriteContentstreams = () => {
    if (favoriteShow.value && loading.value === false) {
        if (store.getters['settings/isContentLoaded']('favourites').data?.length === favoriteContentstreams.value) {
            favoriteContentstreams.value = store.getters['contentstreams/getByKey']('favourites').data;
            loading.value = false;
        } else {
            loading.value = true;
            // Dispatch action to load data if not loaded yet
            store
                .dispatch('contentstreams/favourites', {
                    params: {
                        'sort[by]': 'published_at',
                        'sort[order]': 'desc',
                        page_size: 50,
                        page: 1,
                        key: 'favourites'
                    }
                })
                .then(async () => {
                    favoriteContentstreams.value = store.getters['contentstreams/getByKey']('favourites').data;
                    loading.value = false;
                });
        }
    }
};
const reportsByYear = computed(() => {
    const groups = {};
    for (const report of specialReports.value || []) {
        const year = new Date(report.published_at).getFullYear();
        (groups[year] ||= []).push(report);
    }

    Object.values(groups).forEach((arr) => arr.sort((a, b) => new Date(b.published_at) - new Date(a.published_at)));

    return Object.keys(groups)
        .map(Number)
        .sort((a, b) => b - a)
        .map((year) => ({ year, items: groups[year] }));
});

const handleHash = () => {
    SetAllTapsToFalse();

    if (window.location.hash === '#special-reports' || window.location.hash === '#specialreportsgrid') {
        specialReportsShow.value = true;
    } else {
        marketProductsShow.value = true;
    }
};

onMounted(async () => {
    /**
     * Überprüfen Sie, ob heute vor dem 12.01.2025 ist, und laden Sie dann die Sonderberichte.
     * Kann nach Sonntag (12.01.2025) wieder entfernt werden.
     * Bitte unteren block auch beachten.
     **/

    const today = new Date();
    const date = new Date('2025-01-12');

    if (today <= date) {
        specialReports.value = false;
    } else {
        specialReports.value = true;
        await getSpecialReports();
    }

    /** END **/

    /**
     *  Kann nach Sonntag (12.01.2025) wieder einkommentiert werden.
     **/

    // await getSpecialReports();

    /** END **/

    await getSpecialAnalyses();

    store.dispatch('contentstream_user/getAll').then(() => {
        seenContentstreams.value = store.getters['contentstream_user/get'];

        if (seenContentstreams.value.length > 0) {
            seenContentstreams.value = seenContentstreams.value.filter((f) => f.is_favorite === true);
        }
    });

    handleHash();
});

router.afterEach((to, from) => {
    if (to.params.id === from.params.id) {
        handleHash();
    }
});
</script>

<template class="bg-dashboard-primary">
    <div class="bg-dashboard-primary h-full m-0 pt-10">
        <Loading class="min-h-screen" :loading="loading">
            <div class="bg-white p-10 mx-5 sm:mx-20 rounded-xl">
                <!-- Tab Navigation -->
                <div class="flex flex-col 2xl:flex-row mt-6 mb-20 gap-6">
                    <div class="flex md:flex-row flex-col justify-start gap-4 xl:gap-12 leading-8 text-sm md:text-base font-semibold w-full">
                        <p
                            class="md:hover:underline underline-offset-8 hover:bg-blue md:hover:bg-transparent hover:text-white md:hover:text-black rounded-xl px-2 duration-300 hover:scale-105 md:hover:scale-100"
                            :class="[marketProductsShow ? 'md:underline md:bg-transparent bg-blue text-white md:text-black rounded-xl duration-300' : '', 'cursor-pointer']"
                            @click="
                                SetAllTapsToFalse();
                                marketProductsShow = true;
                            "
                        >
                            {{ $t('nav_analysis') }}
                        </p>

                        <p
                            class="md:hover:underline underline-offset-8 hover:bg-blue md:hover:bg-transparent hover:text-white md:hover:text-black text-ellipsis rounded-xl px-2 duration-300 hover:scale-105 md:hover:scale-100 cursor-pointer whitespace-nowrap"
                            :class="[specialNewsShow ? 'md:underline md:bg-transparent bg-blue text-white md:text-black rounded-xl duration-300' : '']"
                            @click="
                                SetAllTapsToFalse();
                                specialNewsShow = true;
                            "
                        >
                            {{ $t('contenstream_category_special') }}
                        </p>

                        <p
                            v-if="specialReports"
                            class="relative flex md:hover:underline underline-offset-8 hover:bg-blue md:hover:bg-transparent hover:text-white md:hover:text-black text-ellipsis rounded-xl px-2 duration-300 hover:scale-105 md:hover:scale-100 cursor-pointer whitespace-nowrap"
                            :class="[specialReportsShow ? 'md:underline md:bg-transparent bg-blue text-white md:text-black rounded-xl duration-300' : '']"
                            @click="
                                SetAllTapsToFalse();
                                specialReportsShow = true;
                            "
                        >
                            {{ $t('contenstream_category_special_reports') }}
                        </p>

                        <p
                            class="flex flex-row gap-2 justify-between md:justify-center hover:bg-blue md:hover:bg-transparent hover:text-white md:hover:text-black rounded-xl hover:scale-105 md:hover:scale-100 duration-300"
                        >
                            <span
                                class="md:hover:underline underline-offset-8 rounded-xl px-2 duration-300 w-full md:w-auto"
                                :class="[favoriteShow ? 'md:underline md:bg-transparent bg-blue text-white md:text-black rounded-xl duration-300' : '', ' cursor-pointer']"
                                @click="
                                    SetAllTapsToFalse();
                                    favoriteShow = true;
                                    getFavoriteContentstreams();
                                "
                            >
                                {{ $t('contenstream_category_favorites') }}
                            </span>
                        </p>
                    </div>

                    <!-- Search -->
                    <div v-if="!specialNewsShow && !favoriteShow && !specialReportsShow" class="flex flex-row gap-2 items-center min-w-[250px] w-full md:justify-end px-2">
                        <div class="w-full flex flex-row group">
                            <input
                                v-model="searchProductGroupsValue"
                                type="text"
                                :palceholder="$t('plain_search')"
                                class="input block p-2 pl-4 appearance-none focus:outline-none bg-dashboard-primary rounded-l-xl text-xxs flex-1 group-hover:shadow-md duration-300 transition"
                            />
                            <i
                                class="fi fi-rr-search px-3 py-2 text-xxs border-l rounded-r-xl bg-dashboard-primary group-hover:shadow-md duration-300 transition text-dashboard-lightGray"
                            ></i>
                        </div>
                    </div>
                </div>

                <!-- Analysen -->
                <MarketProducts v-show="marketProductsShow" v-model="searchProductGroupsValue" />

                <!-- <div v-if="productGroups.length===0" >
                    <no-item-found class="h-[400px]" item-name="Analysen" />
                </div> -->

                <!-- Search Results -->
                <MarketSearch v-model="searchProductGroupsValue" />

                <!-- Favoriten -->
                <div :class="[favoriteShow ? 'grid' : 'hidden', 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 justify-items-center  p-4']">
                    <template v-if="favoriteContentstreams.length > 0">
                        <div
                            v-for="item in favoriteContentstreams.sort((a, b) => new Date(b.published_at) - new Date(a.published_at))"
                            :key="item.id"
                            class="box h-auto w-full max-w-[300px] justify-self-center group relative fade-in border-0 flex flex-col bg-white rounded-xl shadow-md hover:shadow-lg hover:scale-105 transition-all"
                        >
                            <router-link :to="'/dashboard/markets-analysis/' + item.id" class="contents">
                                <!-- Bildbereich -->
                                <div
                                    class="rounded-t-xl flex justify-center bg-blue-light duration-300 backdrop-blur-md"
                                    :style="{ backgroundColor: getBackgroundColor(item.product?.productGroups?.data[0]?.productCategories, true) }"
                                >
                                    <img :src="item.product.image.replace('.png', '-gradient.png')" class="h-16 w-auto duration-200 object-contain m-4" alt="Product Image" />
                                </div>

                                <!-- Textbereich -->
                                <div class="flex flex-col items-center justify-center px-4 py-4 text-center">
                                    <strong v-if="item?.product" class="text-base font-semibold">
                                        {{ $translate(item.product.name) }}
                                    </strong>
                                    <p v-if="item.title && item.title.de !== item?.product?.name.de" class="text-sm text-gray-600">
                                        {{ $translate(item.title) }}
                                    </p>
                                    <small class="block text-xs text-gray-500">
                                        {{ item.published_at ? formatTimestamp(item.published_at) : '' }}
                                    </small>
                                    <small class="block text-xxs text-dashboard-primary-grey">
                                        {{ $translate(item.contentstreamType.title) }}
                                    </small>
                                </div>
                            </router-link>
                        </div>
                    </template>

                    <div v-else class="text-lg text-gray-500 text-center w-full">
                        {{ $t('no_favorites_selected') }}
                    </div>
                </div>

                <!-- Sondernachrichten -->
                <div :class="[specialNewsShow ? 'grid' : 'hidden', 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 justify-items-center  p-4']">
                    <template v-if="specialAnalyses.length > 0">
                        <div
                            v-for="item in specialAnalyses.sort((a, b) => new Date(b.published_at) - new Date(a.published_at))"
                            :key="item.id"
                            class="box h-auto w-full max-w-[300px] justify-self-center group relative fade-in border-0 flex flex-col bg-white rounded-xl shadow-md hover:shadow-lg hover:scale-105 transition-all"
                        >
                            <router-link :to="'/dashboard/markets-analysis/' + item.id" class="contents">
                                <div class="rounded-t-xl flex justify-center bg-[#0000000d] duration-300 backdrop-blur-md">
                                    <i class="fi fi-tr-square-star text-6xl p-4"></i>
                                </div>

                                <div class="flex flex-col items-center justify-center px-4 py-4 text-center">
                                    <strong v-if="item.title" class="text-base font-semibold">
                                        {{ $translate(item.title) }}
                                    </strong>
                                    <small v-if="item?.published_at" class="block text-xs text-gray-500">
                                        {{ formatTimestamp(item.published_at) }}
                                    </small>
                                </div>
                            </router-link>
                        </div>
                    </template>

                    <div v-else class="text-lg text-gray-500 text-center w-full">
                        {{ $t('no_special_news') }}
                    </div>
                </div>

                <!-- Special Reports -->
                <template v-if="specialReportsShow">
                    <!-- Timeline View (current year) -->
                    <div v-if="region === 'de'" class="mb-12">
                        <SpecialReportsTimeline />
                    </div>

                    <!-- All Reports Grid View -->
                    <template v-if="reportsByYear.length > 0">
                        <div class="space-y-8">
                            <h2 id="specialreportsgrid" class="text-2xl font-bold text-gray-800 mb-4">{{ $t('all_reports') }}</h2>
                            <section v-for="section in reportsByYear" :key="section.year">
                                <!-- Jahres-Header -->
                                <p class="font-bold text-2xl mb-3">
                                    {{ section.year }}
                                </p>

                                <!-- Karten des Jahres -->
                                <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 justify-items-center p-2">
                                    <div
                                        v-for="item in section.items"
                                        :key="item.id"
                                        class="box h-auto w-full max-w-full justify-self-center group relative fade-in border-0 flex flex-col bg-white rounded-xl shadow-md hover:shadow-lg hover:scale-105 transition-all"
                                    >
                                        <router-link :to="'/dashboard/special-report/' + item.id" class="contents">
                                            <div class="flex justify-center bg-[#0000000d] duration-300 backdrop-blur-md rounded-t-xl">
                                                <img v-if="item.thumbnail" :src="item.thumbnail" class="h-full w-full object-cover rounded-t-xl" alt="Product Image" />
                                                <i v-else class="fi fi-tr-newspaper text-6xl p-6 sm:p-10 text-center h-full w-full rounded-t-xl"></i>
                                            </div>

                                            <div class="flex flex-col items-center justify-center px-4 py-4 text-center">
                                                <strong v-if="item.title" class="text-base font-semibold">
                                                    {{ $translate(item.title) }}
                                                </strong>
                                                <small v-if="item?.published_at" class="block text-xs text-gray-500">
                                                    {{ formatTimestamp(item.published_at) }}
                                                </small>
                                            </div>
                                        </router-link>
                                    </div>
                                </div>
                            </section>
                        </div>
                    </template>

                    <div v-else class="text-lg text-gray-500 text-center min-h-[60vh]">
                        {{ $t('no_special_reports') }}
                    </div>
                </template>

                <!-- Shopping Icon -->
                <div class="fixed bottom-10 z-50 inline-block cursor-pointer">
                    <router-link to="/produkte">
                        <div class="h-full rounded-xl duration-500 hover:rotate-90 flex flex-col items-center justify-center gap-3">
                            <i class="fi fi-rr-plus border-0 bg-blue text-white p-3 rounded-xl leading-[0]"></i>
                        </div>
                    </router-link>
                </div>
            </div>
        </Loading>
    </div>
</template>
